import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { SocialMediaService } from '../instances/social-media';
import { SocialMediaConverter } from '@/converters';
import { PutMediaSocialMediaFeedRequest, PutMediaSocialMediaLinkRequest } from '../request/social-media';

export const useSocialMediaAddress = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_SOCIAL_MEDIA_ADDRESS', params],
    async () => {
      const [err, data] = await SocialMediaService.getSocialMediaAddress();
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromGetAddressResponse(data);
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useSocialMedia = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_SOCIAL_MEDIA', params],
    async () => {
      const [err, data] = await SocialMediaService.getSocialMedia();
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromGetResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutSocialMediaLinkMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SOCIAL_MEDIA_LINK'],
    mutationFn: async (payload: PutMediaSocialMediaLinkRequest) => {
      const [err, data] = await SocialMediaService.putSocialMedia(payload);
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromPutResponse(data);
    },
  });
};

export const usePutSocialMediaFeedMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SOCIAL_MEDIA_FEED'],
    mutationFn: async (payload: PutMediaSocialMediaFeedRequest) => {
      const [err, data] = await SocialMediaService.putSocialMedia(payload);
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useGetSocialMediaPostsVirais = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_SOCIAL_MEDIA_POSTS_VIRAIS', params],
    async () => {
      const [err, data] = await SocialMediaService.getSocialMediaPostsVirais(params);
      if (err) {
        throw err;
      }
      return {
        data: SocialMediaConverter.toDomainFromGetPostsViraisResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetPostsViraisById = () => {
  return useMutation({
    mutationKey: ['GET_SOCIAL_MEDIA_POSTS_VIRAIS_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await SocialMediaService.getPostsViraisById(uuid);
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromGetPostsViraisByIdResponse(data);
    },
  });
};

export const usePostPostsViraisMutation = () => {
  return useMutation({
    mutationKey: ['POST_SOCIAL_MEDIA_POSTS_VIRAIS'],
    mutationFn: async (payload: any) => {
      const [err, data] = await SocialMediaService.postPostsVirais(payload);
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromPostPostsViraisResponse(data);
    },
  });
};

export const usePutPostsViraisMutation = () => {
  return useMutation({
    mutationKey: ['PUT_SOCIAL_MEDIA_POSTS_VIRAIS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await SocialMediaService.putPostsVirais(id, payload);
      if (err) {
        throw err;
      }
      return SocialMediaConverter.toDomainFromPutPostsViraisResponse(data);
    },
  });
};

export const useDeletePostsViraisMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_SOCIAL_MEDIA_POSTS_VIRAIS'],
    mutationFn: async (id: string) => {
      const [err] = await SocialMediaService.deletePostsVirais(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
